<template>
  <div class="position-relative" id="subheader">
    <!-- Login and language switch of jobnet.works are left out: PagesNavigation has both -->
    <b-navbar toggleable="lg" class="wrktp-header" :aria-label="content.mainNavLabel">
      <b-container class="wrktp-header__container">
        <b-navbar-brand :href="siteHome" class="wrktp-header__brand p-0 m-0">
          <img :src="require('@/assets/img/logo/logo_wrktp.jpg')" alt="Jobnet.Works" class="wrktp-header__logo" />
        </b-navbar-brand>

        <b-navbar-toggle target="wrktp-header-collapse" :label="content.toggleNavLabel" />

        <b-collapse id="wrktp-header-collapse" is-nav>
          <b-navbar-nav class="ml-auto wrktp-header__nav">
            <b-nav-item :href="url(nav.welcome)" target="_blank" rel="noopener" link-classes="wrktp-header__link">
              {{ nav.welcome.text }}
            </b-nav-item>
            <b-nav-item :href="url(nav.recruitment)" target="_blank" rel="noopener" link-classes="wrktp-header__link">
              {{ nav.recruitment.text }}
            </b-nav-item>
            <b-nav-item :href="url(nav.jobPlacement)" target="_blank" rel="noopener" link-classes="wrktp-header__link">
              {{ nav.jobPlacement.text }}
            </b-nav-item>
            <b-nav-item :href="url(nav.avgsMpav)" target="_blank" rel="noopener" link-classes="wrktp-header__link">
              {{ nav.avgsMpav.text }}
            </b-nav-item>
            <b-nav-item :href="url(nav.itOutstaffing)" target="_blank" rel="noopener" link-classes="wrktp-header__link">
              {{ nav.itOutstaffing.text }}
            </b-nav-item>
            <!-- All menu items open in a new tab, the portal itself stays open -->
            <!-- This portal is "Jobtalent Space", so it is the current page of the menu -->
            <b-nav-item
              :href="talentSpaceUrl"
              target="_blank"
              rel="noopener"
              link-classes="wrktp-header__link active"
              :link-attrs="{ 'aria-current': 'page' }"
            >
              Jobtalent Space
            </b-nav-item>
            <b-nav-item :href="url(nav.aboutUs)" target="_blank" rel="noopener" link-classes="wrktp-header__link">
              {{ nav.aboutUs.text }}
            </b-nav-item>
            <b-nav-item :href="url(nav.contact)" target="_blank" rel="noopener" link-classes="wrktp-header__link">
              {{ nav.contact.text }}
            </b-nav-item>
          </b-navbar-nav>
        </b-collapse>
      </b-container>
    </b-navbar>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { AppModule } from '@/store/modules/app';
import {
  WrktpLink, wrktpContent, wrktpLang, wrktpTalentSpaceUrl, wrktpUrl,
} from '@/config/wrktp';

@Component
export default class HeaderInformationNavigationWrktp extends Vue {
  get lang() {
    return wrktpLang(AppModule.language);
  }

  get content() {
    return wrktpContent[this.lang];
  }

  get nav() {
    return this.content.nav;
  }

  get siteHome() {
    return wrktpUrl(this.lang, '');
  }

  get talentSpaceUrl() {
    return wrktpTalentSpaceUrl(this.lang);
  }

  url(link: WrktpLink) {
    return wrktpUrl(this.lang, link.path);
  }
}
</script>

<style scoped lang="scss">
$wrktp-orange: #f1b034;
$wrktp-orange-light: #fdf5e4;
$wrktp-text: #1a1a1a;
$wrktp-border: #c5c6c8;

// Same prefix as the global portal styles, otherwise their link/heading/nav-link rules win
[theme="default"] body.portal {
  #subheader {
    background-color: #ffffff;
  }

  .wrktp-header {
    font-family: Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    background-color: #ffffff;
    border-bottom: 1px solid $wrktp-border;
    padding: 20px 0;

    // Bootstrap drops .navbar > .container padding below the expand breakpoint
    &__container {
      padding-left: 15px;
      padding-right: 15px;
    }

    &__logo {
      display: block;
      width: 160px;
      height: auto;
      @media (min-width: 992px) {
        width: 260px;
      }
    }

    // Eight items do not fit next to the logo on narrow desktops (e.g. Ukrainian at 1024px),
    // so the menu wraps onto a second row, like on jobnet.works
    &__nav {
      flex-wrap: wrap;
      justify-content: flex-end;
      align-items: center;
      gap: 0.75rem 0.25rem;
    }

    ::v-deep {
      .wrktp-header__link {
        position: relative;
        display: flex;
        align-items: center;
        padding: 0.25rem 0.75rem;
        border-radius: 4px;
        color: $wrktp-text;
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1;
        white-space: nowrap;
        text-decoration: none;
        // Portal styles add a 5px grey bottom border to every .nav-link
        border-bottom: 0;
        transition: all 0.2s ease;

        &:hover,
        &:focus-visible {
          color: $wrktp-orange;
          background-color: $wrktp-orange-light;
          text-decoration: none;
          border-bottom: 0;
        }

        &.active {
          color: $wrktp-orange;
          font-weight: 600;
        }

        // Underline under hovered/active items, like on jobnet.works
        &:hover::after,
        &.active::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -8px;
          width: 100%;
          height: 2px;
          border-radius: 1px;
          background-color: $wrktp-orange;
          pointer-events: none;
        }
      }

      .navbar-toggler {
        border-color: $wrktp-border;
      }

      // Collapsed menu on phones/tablets: full-width rows, no underline
      @media (max-width: 991.98px) {
        .wrktp-header__nav {
          align-items: stretch;
          padding-top: 1rem;
        }

        .wrktp-header__link {
          padding: 1rem;
          font-weight: 600;

          &::after {
            display: none;
          }
        }
      }
    }
  }
}
</style>
